import "../../../styles/globals.css";
import "react-circular-progressbar/dist/styles.css";

import { useQuery } from "@apollo/client";
import { GetStaticPaths, GetStaticProps } from "next";
import { Italiana } from "next/font/google";
import Head from "next/head";
import { useRouter } from "next/router";
import { NextSeo, NextSeoProps } from "next-seo";
import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { withKeepAlive } from "react-next-keep-alive";

import {
  addApolloState,
  cache,
  createApolloClientWithEmptyCache,
  initializeApollo,
} from "@/apollo-client";
import Loading from "@/components/core/loading";
import type { Brand } from "@/components/page/brands/brands-section";
import Header from "@/components/ui/header";
import ProductList from "@/components/ui/product-list";
import ReturnTopButton from "@/components/ui/return-top-button";
import SearchBar from "@/components/ui/search-bar";
import BRAND_ITEMS from "@/graphql/queries/brandItems";
import BRANDS from "@/graphql/queries/brands";
import { Item, Search_Item_FieldsFragment } from "@/graphql/types/graphql";
import useTracker from "@/hooks/useTracker";
import { decodeURL, encodeURL } from "@/utils/index";
import { searchResultRoute } from "@/utils/search";
import { createStructuredData } from "@/utils/structuredData";

const FIRST = 40;

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
});

type Results = {
  cursor: string;
  node: Item;
};

interface BrandPageProps {
  preResults: any;
  brandName: string;
  brandId: string;
}

const BrandPage = ({ preResults, brandName, brandId }: BrandPageProps) => {
  const metadata: NextSeoProps = {
    title: `${brandName} | Plush, AI-Powered Fashion Search and Discovery Platform`,
    description: `Discover Products from ${brandName} on Plush - the curated and personalized AI fashion search platform that lets you shop in natural language.`,
    canonical: `https://www.plush.shop/brand/${encodeURL(
      brandName
    )}/${brandId}`,
    openGraph: {
      title: `${brandName}, Plush, AI-Powered Fashion Search and Discovery Platform`,
      description: `Discover Products from ${brandName} on Plush - the curated and personalized AI fashion search platform that lets you shop in natural language.`,
      images: [{ url: "https://www.plush.shop/social-post.png", alt: "Plush" }],
      siteName: "Plush",
      type: "website",
    },
  };

  const apolloClient = initializeApollo();

  const router = useRouter();
  const { searchEvent, brandPageViewEvent } = useTracker();
  const [searchValue, setSearchValue] = useState("");

  const [results, setResults] = useState<Results[] | undefined>(
    preResults?.edges || []
  );
  // The state has the same value as `loading` in `useQuery` except the init value,
  // the reason to add this state it is to allow page to be rendered in server side.

  const [hasNextPage, setHasNextPage] = useState<boolean | undefined>(
    preResults?.pageInfo?.hasNextPage || false
  );
  const returnTopRef = useRef<HTMLDivElement>(null);
  const searchbarRef = useRef<HTMLDivElement>(null);

  const { data, refetch } = useQuery(BRAND_ITEMS, {
    variables: { brandId, first: FIRST },
    skip: !brandId,
    errorPolicy: "ignore",
    fetchPolicy: "cache-only",
  });

  const items = React.useMemo(() => {
    return (results || []).map((result) => {
      return result.node;
    });
  }, [results]);

  const mergeAndRemoveDuplicates = (arr1: Results[], arr2: Results[]) => {
    const combineArray = [...arr1, ...arr2];
    const map = new Map();

    combineArray.forEach((item) => {
      map.set(item.node.id, item);
    });

    return Array.from(map.values());
  };

  const loadMore = async () => {
    if (!hasNextPage) return;
    if (!brandId) return;

    const { data } = await apolloClient.query({
      query: BRAND_ITEMS,
      variables: {
        brandId,
        first: FIRST,
        after:
          results && results.length !== 0
            ? results[results.length - 1].cursor
            : undefined,
      },
    });
    if (!data) return;

    const goodsList = data?.brandItems?.edges;
    const newHasNextPage = data?.brandItems?.pageInfo?.hasNextPage || false;
    cache.updateQuery(
      {
        query: BRAND_ITEMS,
        variables: { brandId, first: FIRST },
      },
      (data) => {
        const newResults = mergeAndRemoveDuplicates(
          data?.brandItems?.edges || [],
          goodsList || []
        );
        return {
          ...data,
          brandItems: {
            ...(data?.brandItems || {}),
            edges: newResults,
            pageInfo: {
              hasNextPage: newHasNextPage,
            },
          },
        };
      }
    );
  };

  // Edit Search
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleFormSubmit = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    if (!searchValue) {
      return;
    }
    if (router) {
      searchEvent({
        query: searchValue,
        smart_filter_content: "",
        original_query: searchValue,
      });

      setResults(undefined);
      await router.push(searchResultRoute(searchValue));
    }
  };

  const searchBarValue = Array.isArray(searchValue)
    ? searchValue[0]
    : searchValue || "";

  const handleScroll = () => {
    if (returnTopRef && returnTopRef.current) {
      const scrollTopButton = returnTopRef.current;

      scrollTopButton.style.display = window.scrollY > 200 ? "block" : "none"; // 页面滚动超过200px时显示按钮
    }
  };

  const productClickHandler = (node: Search_Item_FieldsFragment) => {
    router.push(`/product${node.seoURL}`);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Scroll to Top
  useEffect(() => {
    // 添加滚动事件监听器
    window.addEventListener("scroll", handleScroll);

    // 清理函数：移除监听器，防止内存泄漏
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // 空依赖数组意味着这个effect只会在组件挂载和卸载时执行

  useEffect(() => {
    if (data) {
      setHasNextPage(data.brandItems?.pageInfo?.hasNextPage);
      setResults(data.brandItems?.edges || []);
    }
  }, [data]);

  useEffect(() => {
    cache.updateQuery(
      {
        query: BRAND_ITEMS,
        variables: { brandId, first: FIRST },
      },
      (data) => {
        return {
          ...data,
          brandItems: preResults,
        };
      }
    );
  }, [brandId, preResults]);

  useEffect(() => {
    brandPageViewEvent({ brand_id: brandId, brand_name: brandName });
    // SSG need to refetch query to update any info needs auth, like "saved".
    refetch();
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            createStructuredData({
              description: metadata.description,
              url: metadata.canonical,
            })
          ),
        }}
      />
      <NextSeo {...metadata} />
      <Head>
        <title>{metadata.title}</title>
      </Head>
      <InfiniteScroll
        hasMore={!!hasNextPage}
        loadMore={loadMore}
        loader={undefined}
      >
        <main className="flex flex-col items-center min-h-screen bg-white relative z-10">
          <div className="cover"></div>
          <Header position="relative" />
          <div className="section-container bg-white search_input_postion">
            <div id="searchBar" ref={searchbarRef}>
              <SearchBar
                value={searchBarValue}
                position="relative"
                onChange={handleSearchChange}
                onSubmit={handleFormSubmit}
                defaultSingleRow="true"
                defaultPlaceholder={`Try searching “long white dress from ${brandName}”`}
              />
            </div>
          </div>

          {!results || results.length === 0 ? (
            <div
              className={
                italiana.className + " section-container search_result_text"
              }
              style={{
                color: "#191919",
                marginTop: 180,
                marginBottom: 180,
              }}
            >
              No results found
            </div>
          ) : (
            <React.Fragment>
              <h1
                className={`${italiana.className} section-container brand-name`}
              >
                {brandName}
              </h1>
              <div className="section-container goods_over_style">
                <ProductList
                  items={items}
                  onClickProduct={productClickHandler}
                />
              </div>

              <ReturnTopButton />
            </React.Fragment>
          )}
          {hasNextPage && results && results.length > 0 && (
            <div className="page-loading">
              <Loading />
            </div>
          )}
          <style jsx>{`
            .page-loading {
              padding-bottom: 56px;
            }
            .loading-screen {
              margin-top: 24px;
              justify-content: center;
            }
            .search_input_postion {
              position: sticky;
              padding: 32px 72px 24px;
              z-index: 10;
              top: 0;
            }

            .goods_over_style {
              width: 100%;
              display: flex;
              flex-wrap: wrap;
              padding: 0 72px;
              margin-top: 16px;
            }

            .goods-item-span {
              margin-bottom: 56px;
              margin-right: 2.6%;
              width: 23%;
              cursor: pointer;

              &:nth-child(4n) {
                margin-right: 0;
              }
            }

            .modalshow {
              display: block;
            }
            .modalHide {
              display: none;
            }

            .returnToTop_class {
              display: block;
              position: fixed;
              right: 24px;
              bottom: 24px;
              width: 40px;
              height: 40px;
              display: none;
              cursor: pointer;
            }

            .search_result_text {
              text-align: center;
              font-size: 64px;
            }

            .loading-progress {
              width: 120px;
              height: 120px;
              margin-top: -350px;
            }

            .brand-name {
              text-align: left;
              width: 100%;
              margin-top: 22px;
              margin-bottom: 4px;
              padding: 0 72px;

              color: #191919;
              font-size: 40px;
              font-style: normal;
              font-weight: 400;
              line-height: normal;
            }
            @media (max-width: 768px) {
              .page-loading {
                padding-bottom: 24px;
              }
              .search_result_text {
                width: 286px;
                font-size: 38px;
              }

              .goods_over_style {
                width: 100%;
                margin: 0;
                padding-left: 24px;
                padding-right: 24px;
                justify-content: space-between;
              }
              .goods-item-span {
                width: calc(50% - 8px);
                margin-left: 0;
                margin-right: 0;
                margin-bottom: 24px;
              }
              .cover {
                position: fixed;
                width: 100%;
                background-color: #fff;
                z-index: 50;
                height: 32px;
                top: 0;
                left: 0;
              }
              .search_input_postion {
                width: 100%;
                padding-left: 24px;
                padding-right: 24px;
                margin-top: 0px;
                padding-bottom: 24px;
                padding-top: 0px;
                top: 32px;
              }

              .loading-screen {
                justify-content: flex-start;
              }
              .loading-progress {
                width: 76px;
                height: 76px;
                margin-top: 100px;
              }
              .brand-name {
                font-size: 32px;
                padding: 0 24px;
                margin-top: 0px;
                margin-bottom: 20px;
              }
            }
          `}</style>
          <style global jsx>{`
            .results-search-bar {
              @media (max-width: 768px) {
                height: 56px;
              }
            }
          `}</style>
        </main>
      </InfiniteScroll>
    </>
  );
};

// @ts-expect-error router query is always string at here
export default withKeepAlive(BrandPage, ({ router }) => router.query.id, {
  // Disabled this to avoid conflict with the nextjs scroll restoration
  keepScrollEnabled: false,
});

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NEXT_PUBLIC_SSG === "false") {
    return { paths: [], fallback: "blocking" };
  }
  // Create a new client instance instead of using the global client.
  // Using the global client will include brand query results in the SSG page,
  // significantly increasing the page size.
  const apolloClient = createApolloClientWithEmptyCache();

  const { data } = await apolloClient.query({
    query: BRANDS,
  });

  const paths = data?.brands?.map((brand: Brand) => {
    return {
      params: {
        id: brand.id,
        name: encodeURL(brand.name),
      },
    };
  });

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps<Brand> = async ({ params }) => {
  if (!params) return;

  const apolloClient = createApolloClientWithEmptyCache();

  try {
    const { data } = await apolloClient.query({
      query: BRAND_ITEMS,
      variables: {
        brandId: params.id,
        first: FIRST,
      },
    });

    return addApolloState(apolloClient, {
      props: {
        brandId: params.id,
        brandName: decodeURL(params.name as string),
        preResults: data?.brandItems,
      },
      revalidate: 60 * 60 * 24,
    });
  } catch (e) {
    return addApolloState(apolloClient, {
      props: {
        brandId: params.id,
        brandName: decodeURL(params.name as string),
        preResults: null,
      },
      revalidate: 60 * 60 * 24,
    });
  }
};
